import {
    EllipsePointMarker,
    FastLineRenderableSeries,
    IOverviewOptions,
    IRenderableSeries,
    ModifierMouseArgs,
    NumberRange,
    OverviewRangeSelectionModifier,
    Thickness,
    XyScatterRenderableSeries,
} from "scichart";
import { GetChartTheme } from "./ChartConfiguration";

const theme = GetChartTheme();

// Overview range-selection modifier that resets the selection to the full
// data extent (i.e. zooms the parent chart out on X) when the overview is
// double-clicked. The base modifierDoubleClick is a no-op.
class DoubleClickResetOverviewModifier extends OverviewRangeSelectionModifier {
    modifierDoubleClick(args: ModifierMouseArgs) {
        super.modifierDoubleClick(args);
        // The overview X axis is auto-ranged to the full data extent.
        const xAxis = this.parentSurface?.getXAxisById(this.xAxisId);
        if (!xAxis) {
            return;
        }
        const fullRange = xAxis.visibleRange;
        this.selectedArea = fullRange;            // move the overview selection box
        this.onSelectedAreaChanged?.(fullRange);  // drive the main chart's X range
    }
}

// options used for current example
export const overviewOptions: IOverviewOptions = {
    theme: theme,
    disableAspect: true,
    padding: Thickness.fromNumber(5),

    // Copies land on the overview's own Y axis, so a series sitting at the range maximum
    // would be drawn flush against the top edge. Pad the range to keep it inside.
    overviewYAxisOptions: { growBy: new NumberRange(0.2, 0.2) },

    transformRenderableSeries: (renderableSeries: IRenderableSeries) => {
        const wasmContext = renderableSeries.parentSurface.webAssemblyContext2D;

        // Hidden series are still copied — the timeline's hidden filter-period series is what holds
        // the overview's X range to the full period instead of letting it collapse onto the data
        // extent — but they are drawn with no ink, as they are in the chart.
        if (!renderableSeries.isVisible) {
            return new FastLineRenderableSeries(wasmContext, {
                dataSeries: renderableSeries.dataSeries,
                stroke: "transparent",
            });
        }

        // Timeline charts plot discrete events as scatter: copied as a line they draw nothing,
        // which is why their overview came out empty. Keep scatter as scatter.
        if (renderableSeries instanceof XyScatterRenderableSeries) {
            return new XyScatterRenderableSeries(wasmContext, {
                dataSeries: renderableSeries.dataSeries,
                pointMarker: new EllipsePointMarker(wasmContext, {
                    width: 4,
                    height: 4,
                    fill: "rgba(50, 53, 67, 0.65)",
                    strokeThickness: 0,
                }),
            });
        }

        const copiedSeries = new FastLineRenderableSeries(wasmContext, {
            dataSeries: renderableSeries.dataSeries,
            stroke: "rgba(50, 53, 67, 0.65)",
        });

        return copiedSeries;
    },
};

// Same as overviewOptions, but double-clicking the overview resets the X range.
// A factory (not a constant) so each chart mount gets its own modifier instance
// rather than sharing one across surfaces.
export const getResettableOverviewOptions = (): IOverviewOptions => ({
    ...overviewOptions,
    customRangeSelectionModifier: new DoubleClickResetOverviewModifier(),
});

// options used for current example
export const endpointReportOverviewOptions: IOverviewOptions = {
  theme: theme,
  disableAspect: true,
  padding: new Thickness(0, 5, 0, 5),

};